/* eslint-disable no-nested-ternary */
import { matchSorter } from 'match-sorter';
import React, { useEffect, useMemo } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  useAsyncDebounce,
  useExpanded, useFilters, useGlobalFilter, usePagination, useSortBy, useTable,
} from 'react-table';
import { useTranslation } from 'react-i18next';
import useWindowDimensions from '../helpers/useWindowDimensions';
import TablePagination from './tablePagination';

function GlobalFilter({
  area,
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  searchSuffix,
  total,
  paginated,
}) {
  const count = paginated ? total : preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((v) => {
    setGlobalFilter(v || undefined);
  }, 200);
  return (
    <div className="relative mr-6 my-2 w-full">
      <input
        id={`txtTableSearch${area}`}
        value={value || ''}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`${count} ${searchSuffix}...`}
        type="search"
        className="rounded-lg border-1 border-gray-200 p-3 w-full pl-8 bg-sentric-menu-gray"
      />
      <div className="absolute top-1/3 left-2 text-gray-100">
        <svg
          version="1.1"
          className="h-4 text-dark"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 52.966 52.966"
          xmlSpace="preserve"
        >
          <path d="M51.704,51.273L36.845,35.82c3.79-3.801,6.138-9.041,6.138-14.82c0-11.58-9.42-21-21-21s-21,9.42-21,21s9.42,21,21,21
        c5.083,0,9.748-1.817,13.384-4.832l14.895,15.491c0.196,0.205,0.458,0.307,0.721,0.307c0.25,0,0.499-0.093,0.693-0.279
        C52.074,52.304,52.086,51.671,51.704,51.273z M21.983,40c-10.477,0-19-8.523-19-19s8.523-19,19-19s19,8.523,19,19
        S32.459,40,21.983,40z"
          />
        </svg>

      </div>
    </div>

  );
}

// Define a default UI for filtering
function DefaultColumnFilter() {
  return null;
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

function Table({
  area = '', columns, data, revealComponent, highlightColour, searchSuffix, disableSearch = false, title = null, bgClass = '', min = 10, noneMessage = null, getRowId = null, disablePagination = false, onRowClick = () => {}, paginated = false, setTableState = () => {}, tableState, total = 0, initialSort, currentpage, filter = () => {}, setFilter = () => {},
}) {
  const filterTypes = () => ({
    // Add a new fuzzyTextFilterFn filter type.
    fuzzyText: fuzzyTextFilterFn,
    // Or, override the default text filter to use
    // "startWith"
    text: (rows, id, filterValue) => rows.filter((row) => {
      const rowValue = row.values[id];
      return rowValue !== undefined
        ? String(rowValue)
          .toLowerCase()
          .startsWith(String(filterValue).toLowerCase())
        : true;
    }),
  });

  const defaultColumn = () => ({
    // Let's set up our default Filter UI
    Filter: DefaultColumnFilter,
  });

  const nextPageState = () => {
    const p = tableState.page;
    setTableState('page', p + 1);
  };

  const setPageSizeState = (size) => {
    setTableState('perPage', size);
  };

  const prevPageState = () => {
    const p = tableState.page;
    setTableState('page', p - 1);
  };

  const { t } = useTranslation();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    preGlobalFilteredRows,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: {
      pageIndex, pageSize, globalFilter, sortBy,
    },

  } = useTable(
    {
      columns,
      data,
      getRowId: getRowId || React.useCallback((row, index) => (index), []),
      autoResetPage: false,
      autoResetExpanded: false,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: {
        pageIndex: currentpage || 0,
        pageSize: min,
        sortBy: useMemo(() => initialSort || [{
          id: 'title',
          desc: false,
        }], []),
        hiddenColumns: ['colGigId', 'colSetlistId'],
      },
      manualPagination: paginated,
      manualSortBy: paginated,

    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy,
    useExpanded,
    usePagination,

  );
  const { width } = useWindowDimensions();
  useEffect(() => {
    if (paginated) {
      setTableState('sortBy', sortBy);
    }
  }, [sortBy]);

  // useEffect(() => {
  //   if (sortBy !== initialSort) {
  //     onSort(sortBy);
  //     setTableState('page', 1);
  //   }
  // }, [sortBy]);

  return (
    <div>
      {!disableSearch
      && (
      <div className="mb-5">
        <GlobalFilter
          area={area}
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={paginated ? filter : globalFilter}
          setGlobalFilter={paginated ? setFilter : setGlobalFilter}
          searchSuffix={searchSuffix}
          total={total}
          paginated={paginated}
        />
      </div>
      )}
      {!!title
         && (
         <div className="w-full lg:w-1/2 flex items-center mb-4">
           <h2 className="text-sentric-table-text font-semibold text-lg">{title}</h2>
           <div className="ml-2 mr-2 inline-block h-5 text-xxs py-0 px-1 rounded uppercase bg-opacity-25 bg-song-sub-color text-song-sub-color">
             {data?.length || 0}
           </div>
         </div>
         )}

      <div className={`text-sentric-table-text ${bgClass}`}>
        {width > 768
        && (
        <table className="min-w-full divide-y divide-gray-200 text-sm" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    className="p-2 text-left text-xxs font-semibold text-sentric-table-gray uppercase tracking-wider"
                    {...column.getHeaderProps({
                      ...column.getSortByToggleProps(),
                      style: { minWidth: column.width, width: column.width, maxWidth: column.width },
                      id: column.id,
                    })}
                  >
                    <span className={`${column.canSort ? 'underline' : ''}`}>
                      {column.render('Header')}
                    </span>
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? <FaChevronDown className="inline-block ml-1" />
                          : <FaChevronUp className="inline-block ml-1" />
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y w-full divide-sentric-border-gray font-semibold" {...getTableBodyProps()}>
            {page.length > 0 && page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.getRowProps().key}>
                  <tr
                    onClick={() => onRowClick(row)}
                    {...(revealComponent ? row.getToggleRowExpandedProps() : [])}
                    className="cursor-pointer hover:bg-sentric-menu-gray"
                    {...row.getRowProps()}
                    id={`btn${area}Row`}
                  >
                    {row.cells.map((cell) => (
                      <td
                        className="whitespace-wrap px-2 py-4"
                        {...cell.getCellProps()}
                      >
                        <div className="truncate whitespace-normal">
                          {cell.render('Cell')}
                        </div>
                      </td>
                    ))}
                  </tr>
                  {row.isExpanded && revealComponent
                  && <>{React.cloneElement(revealComponent, { row })}</>}
                </React.Fragment>
              );
            })}
            {page.length === 0
            && (
              <tr>
                <td colSpan="2">
                  <div className="p-4 w-full">
                    {!noneMessage
                    && (
                    <p>
                      {t('common:table-there-are-no')}
                      {' '}
                      {searchSuffix}
                      .
                    </p>
                    )}
                    {!!noneMessage
                    && (
                    <p>
                      {noneMessage}
                    </p>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        )}
        {width < 768
        && (
        <table className="w-full  divide-y divide-gray-200 text-sm">
          <tbody className="bg-white flex flex-wrap w-full divide-y divide-sentric-border-gray font-semibold ">
            {page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.getRowProps().key}>
                  <tr
                    onClick={() => onRowClick(row)}
                    {...(revealComponent ? row.getToggleRowExpandedProps() : [])}
                    className={`${revealComponent ? 'hover:bg-sentric-menu-gray' : ''} w-full flex`}
                  >
                    <td colSpan="10" className="w-full my-3 relative flex flex-wrap">
                      {row.cells.map((cell) => (
                        <div
                          {...cell.getCellProps()}
                          className="w-full p-1 my-1"
                        >
                          {cell.column.id !== 'expander'
                            && (
                            <div className="flex w-full">
                              <div className="sm:w-3/12 w-5/12 text-xxs font-semibold text-sentric-table-gray uppercase tracking-wider">
                                {cell.column.Header}
                              </div>
                              <div className="flex truncate whitespace-normal w-6/12">
                                {cell.render('Cell')}
                              </div>
                            </div>
                            )}
                          {cell.column.id === 'expander'
                              && (
                              <div className="absolute top-2 right-4">
                                {cell.render('Cell')}

                              </div>
                              )}
                        </div>
                      ))}
                    </td>
                  </tr>
                  {row.isExpanded && revealComponent
                  && <>{React.cloneElement(revealComponent, { row })}</>}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        )}

      </div>
      {!disablePagination
      && (
      <div className={`${bgClass} pt-4 pb-4`}>
        <TablePagination
          previousPage={() => (paginated ? prevPageState() : previousPage())}
          canPreviousPage={paginated ? tableState.page !== 1 : canPreviousPage}
          nextPage={() => (paginated ? nextPageState() : nextPage())}
          canNextPage={paginated ? tableState.page !== Math.ceil(total / tableState.perPage) : canNextPage}
          pageSize={paginated ? tableState.perPage : pageSize}
          setPageSize={(size) => (paginated ? setPageSizeState(size) : setPageSize(size))}
          pageIndex={paginated ? tableState.page - 1 : pageIndex}
          totalPages={paginated ? Math.ceil(total / tableState.perPage) : pageOptions.length}
          highlightColour={highlightColour}
          area={area}
        />
      </div>
      )}
    </div>
  );
}

export default Table;
